import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

import worldMap from "../../../../../data/world-110m.json";

import { userRoleList } from "../../../../User/enums";

import { useGetTalentHeatmap } from "../../../api/queries/useGetTalentHeatmap";
import { formatImgUrl } from "../../../../../utils/cardFormating";

const getPercentageColor = (percentage: number) => {
  // Base color #0D4D3A is HSL(162, 71%, 18%)
  // To avoid 'bright' colors, we reduce saturation for lower percentages
  const saturation = 15 + (percentage / 100) * (71 - 15);
  const lightness = 92 - (percentage / 100) * (92 - 18);
  return `hsl(162, ${saturation}%, ${lightness}%)`;
};

const TalentHeatMap = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "overviewPage" });
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 640, height: 320, scale: 120 });

  const { data, isLoading } = useGetTalentHeatmap();

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        setMapDimensions({ width: newWidth, height: newWidth * 0.5, scale: Math.round(newWidth * 0.1875) });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  if (isLoading) return null;

  const countries = data?.allCountries || [];

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.talentDistributionHeatmapTitle}>{t("talentDistributionHeatmap")}</Typography>
      <Box sx={styles.talentDistributionHeatmapContainerWrapper}>
        <Box ref={containerRef} sx={styles.talentDistributionHeatmapContainer}>
          <ComposableMap
            projectionConfig={{
              center: [0, 0],
              scale: mapDimensions.scale,
            }}
            height={mapDimensions.height}
            width={mapDimensions.width}
          >
            <Geographies geography={worldMap}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const countryStats = countries.find(
                    (c) => c.country.name === geo.properties.name || c.country.name === geo.properties.ISO_A3
                  );
                  const fillColor = countryStats ? getPercentageColor(countryStats.percentage) : "#E1EAE7";

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      // @ts-ignore
                      style={{
                        default: {
                          fill: fillColor,
                          stroke: "#FFF",
                          outline: "none",
                        },
                        hover: {
                          fill: fillColor,
                          stroke: "#FFF",
                          outline: "none",
                        },
                        pressed: {
                          fill: fillColor,
                          stroke: "#FFF",
                          outline: "none",
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>
            {countries.map((countryStats) => (
              <HeatMapMarker
                key={countryStats.country.id}
                data={countryStats}
                coordinates={[countryStats.country.longitude, countryStats.country.latitude]}
              />
            ))}
          </ComposableMap>
        </Box>
        <Box sx={styles.talentDistributionHeatmapCountriesList}>
          {countries.map((countryStats) => (
            <Box key={countryStats.country.id} sx={styles.talentDistributionHeatmapCountriesListItem}>
              {/* Using a placeholder for flag as we don't have the base URL for the provided url string easily accessible here without more investigation, 
                  but showing the name is a safe bet. Or we could try to use the url directly. */}
              <Box
                component="img"
                src={formatImgUrl(countryStats.country.url)}
                sx={{ width: 24, height: 16, objectFit: "cover", borderRadius: "2px" }}
                onError={(e: any) => {
                  e.target.style.display = "none";
                }}
              />
              <Box sx={styles.talentDistributionHeatmapCountriesListItemContent}>
                <Typography sx={styles.talentDistributionHeatmapCountriesListItemContentTitle}>
                  {countryStats.country.name}
                </Typography>
                <Box sx={styles.talentDistributionHeatmapCountriesListItemContentLinearProgress}>
                  <LinearProgress
                    variant="determinate"
                    value={countryStats.percentage}
                    sx={styles.talentDistributionHeatmapCountriesListItemContentLinearProgressBar(
                      countryStats.percentage
                    )}
                  />
                  <Typography sx={styles.talentDistributionHeatmapCountriesListItemContentLinearProgressBarValue}>
                    {countryStats.percentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {!countries.length && (
        <Box sx={styles.noTalentAppliedYetContainer}>
          <Typography sx={styles.noTalentAppliedYet}>{t("noTalentAppliedYet")}</Typography>
          <Typography sx={styles.onceCandidatesApply}>{t("onceCandidatesApply")}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default TalentHeatMap;

const HeatMapMarker = ({ data, coordinates }: { data: any; coordinates: [number, number] }) => {
  const { t: tUserRole } = useTranslation("common", { keyPrefix: "user.role" });

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "transparent",
            p: 0,
            m: 0,
            maxWidth: "none",
            "& .MuiTooltip-arrow": {
              display: "none",
            },
          },
        },
      }}
      title={
        <Box sx={styles.tooltipInnerContainer}>
          <Box sx={styles.tooltipInnerContainerHeader}>
            <Box
              component="img"
              src={formatImgUrl(data.country.url)}
              sx={{ width: 17, height: 12, objectFit: "cover", mr: 1 }}
              onError={(e: any) => {
                e.target.style.display = "none";
              }}
            />
            <Typography sx={styles.tooltipInnerContainerHeaderTitle}>{data.country.name}</Typography>
          </Box>

          <Box sx={styles.tooltipInnerContainerContent}>
            {userRoleList.map((role) => (
              <Box key={role} sx={styles.tooltipInnerContainerContentItem}>
                <Typography sx={styles.tooltipInnerContainerContentItemTitle}>
                  {tUserRole(role.toUpperCase())}
                </Typography>
                <Typography sx={styles.tooltipInnerContainerContentItemValue}>
                  {data[role.toUpperCase()] ?? 0}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      }
    >
      <g>
        <Marker coordinates={coordinates}>
          <circle r={17.5} fill="#0D4D3A" fillOpacity={0.1} />
          <circle r={10.5} fill="#0D4D3A" fillOpacity={0.2} />
          <circle r={3.5} fill="#0D4D3A" />
        </Marker>
      </g>
    </Tooltip>
  );
};

const styles = {
  container: {
    display: "flex",
    padding: "24px",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Base-White, #FFF)",
  },
  talentDistributionHeatmapTitle: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
  talentDistributionHeatmapContainer: {
    width: "100%",
    maxWidth: "640px",
    height: "310px",
    flex: 1,
  },
  talentDistributionHeatmapContainerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    height: { xs: "fit-content", md: "310px" },
    gap: "24px",
    flexDirection: { xs: "column", md: "row" },
  },
  talentDistributionHeatmapCountriesList: {
    width: { xs: "100%", md: "175px" },
    height: { xs: "fit-content", md: "310px" },
    overflow: "auto",
    display: "flex",
    gap: "16px",
    flexDirection: { xs: "row", md: "column" },
    alignItems: { xs: "flex-start", md: "center" },
    pr: "12px",
    pt: "8px",
    pb: "8px",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "2px",
    },
  },
  talentDistributionHeatmapCountriesListItem: {
    width: { xs: "fit-content", md: "100%" },
    display: "flex",
    gap: "8px",
    borderRight: { xs: "1px solid var(--Gray-200, #EAECF0)", md: "none" },
    pr: { xs: "16px", md: "0" },
    "&:last-of-type": {
      borderRight: "none",
      pr: "0",
    },
  },
  talentDistributionHeatmapCountriesListItemContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  talentDistributionHeatmapCountriesListItemContentTitle: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    textWrap: "nowrap",
  },
  talentDistributionHeatmapCountriesListItemContentLinearProgress: {
    flex: 1,
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  talentDistributionHeatmapCountriesListItemContentLinearProgressBar: (percentage: number) => ({
    flex: 1,
    height: "8px",
    borderRadius: "4px",
    "& .MuiLinearProgress-bar": {
      borderRadius: "4px",
      backgroundColor: getPercentageColor(percentage),
    },
    backgroundColor: "hsl(162, 10%, 95%)",
  }),
  talentDistributionHeatmapCountriesListItemContentLinearProgressBarValue: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  tooltipInnerContainer: {
    display: "flex",
    padding: "10px 12px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    borderRadius: "7px",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 10.327px 13.769px -3.442px rgba(16, 24, 40, 0.08), 0 3.442px 5.163px -1.721px rgba(16, 24, 40, 0.03)",
    width: "fit-content",
  },
  tooltipInnerContainerHeader: {
    display: "flex",
    alignItems: "center",
  },
  tooltipInnerContainerContent: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  tooltipInnerContainerContentItem: {
    display: "flex",
    alignItems: "center",
    gap: "3.5px",
  },
  tooltipInnerContainerContentItemTitle: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "14px",
  },
  tooltipInnerContainerContentItemValue: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "14px",
  },
  tooltipInnerContainerHeaderTitle: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "10.4px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "15.49px",
  },
  noTalentAppliedYetContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "center",
  },
  noTalentAppliedYet: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  onceCandidatesApply: {
    color: "var(--Gray-Gray-700, #5E6278)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
};
